import React, { useState, useCallback, useEffect } from 'react';
import { GlobalSearchProvider } from '../../context/GlobalSearchContext';
import { LabProvider } from '../../context/LabContext';
import { PAEContextProvider } from '../../context/PAEContext';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import ImpersonationBanner from '../common/ImpersonationBanner';
import ErrorBoundary from '../common/ErrorBoundary';
import PendingFormsGate from '../forms/PendingFormsGate';
import PAEButton from '../pae/PAEButton';
import FollowUpAlertPoller from '../lis/FollowUpAlertPoller';
import useTechShift from '../../hooks/useTechShift';
import './MainLayout.css';

const MainLayout = ({ currentUser, onLogout }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const location = useLocation();
  // Globally manage technician shift / GPS heartbeat. No-op for non-techs.
  const techShift = useTechShift();

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // Auto-collapse sidebar on mobile when navigating to a new page
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setSidebarCollapsed(true);
    }
  }, [location.pathname]);

  return (
    <LabProvider currentUser={currentUser}>
    <PAEContextProvider>
    <GlobalSearchProvider>
      <div className="main-layout">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          currentUser={currentUser}
          currentPath={location.pathname}
        />
        <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <ImpersonationBanner />
          {techShift.shiftOpen && (
            <div style={{
              padding: '6px 14px',
              background: techShift.pingError ? 'rgba(239,68,68,0.10)' : 'rgba(34,197,94,0.10)',
              borderBottom: `1px solid ${techShift.pingError ? 'rgba(239,68,68,0.4)' : 'rgba(34,197,94,0.4)'}`,
              color: techShift.pingError ? '#b91c1c' : '#15803d',
              fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: techShift.pingError ? '#ef4444' : '#22c55e', display: 'inline-block', animation: 'techPulse 1.6s ease-in-out infinite' }} />
              On shift · live tracking{techShift.lastPingAt ? ` · last ping ${new Date(techShift.lastPingAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })}` : ''}
              {techShift.pingError && <span> · {techShift.pingError}</span>}
              <button onClick={() => techShift.endShift('manual')}
                style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 6, border: '1px solid currentColor', background: 'transparent', color: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                End Shift
              </button>
              <style>{`@keyframes techPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
            </div>
          )}
          <Header
            currentUser={currentUser}
            onLogout={onLogout}
          />
          <div className="page-content">
            {/* THE WHOLE APPLICATION USED TO GO WHITE.
                "i saved the address for the patient and on save the screen
                 went blank, i refreshed it and then it came back"
                One render-time exception anywhere under here unmounted the
                entire tree — sidebar, header and all — leaving a blank page
                with no way out but a reload. The boundary component already
                existed and was used in three places; nothing sat above the
                routes.
                KEYED ON THE PATH so it RESETS when you navigate. A boundary
                that has caught stays caught, so without the key the error
                panel would follow you to every other page and the only cure
                would still be a reload. */}
            <ErrorBoundary key={location.pathname} name="this page">
              <Outlet />
            </ErrorBoundary>
          </div>
        </div>
        {/* First-login signature gate — checks /api/forms/pending and blocks
            the app with a fullscreen modal until every required form is
            signed (PHI Form for Portal Users, BAAs, etc.). Mounted here so
            it's only active for authenticated users (post-login routes). */}
        <PendingFormsGate />
        {/* Floating Pre-Analytical Error reporter — present on every page. */}
        <PAEButton />
        {/* Global popup poller — fires toasts when a call-log follow-up
            assigned to the current user (or their dept) comes due. */}
        <FollowUpAlertPoller />
      </div>
    </GlobalSearchProvider>
    </PAEContextProvider>
    </LabProvider>
  );
};

export default MainLayout;
